// frontend/src/modules/reports/pages/financial/BilledCollectedSeries.tsx
//
// Phase 4 (Reports Frontend), Task 4. Shared billed-vs-collected chart used
// by BOTH RevenuePage and CashTrendPage (DRY per the plan) — built ONCE,
// reused twice. Uses only existing design-system chart primitives
// (TwinLineChart) — no new chart lib. Renders one section PER CURRENCY,
// never summed across currencies.

import * as React from 'react';
import { TwinLineChart, CHART_COLORS } from '../../../../design-system/charts';
import { VISUAL } from '../../../../design-system/visual';
import { formatMoney } from '../../../../design-system/format';
import { groupByCurrency } from './currencyGroup';

export interface BilledCollectedRow {
  bucket: string;
  currency: string;
  billed: number;
  collected: number;
}

export function BilledCollectedSeries({ rows }: { rows: BilledCollectedRow[] }) {
  const sections = groupByCurrency(rows);
  if (sections.length === 0) return null;

  return (
    <div className="space-y-6">
      {sections.map(([currency, currencyRows]) => {
        const totalBilled = currencyRows.reduce((s, r) => s + r.billed, 0);
        const totalCollected = currencyRows.reduce((s, r) => s + r.collected, 0);
        return (
          <div key={currency} className={[VISUAL.card, 'p-6'].join(' ')}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-sans font-semibold text-[15px] text-foreground">{currency}</h3>
              <div className="flex items-center gap-4 text-[12px] font-medium">
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS.CORAL }} />
                  Billed {formatMoney(totalBilled, currency)}
                </span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-ink dark:bg-white" />
                  Collected {formatMoney(totalCollected, currency)}
                </span>
              </div>
            </div>
            <TwinLineChart
              primary={currencyRows.map((r) => r.billed)}
              secondary={currencyRows.map((r) => r.collected)}
              height={160}
            />
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
              {currencyRows.map((r) => (
                <div key={r.bucket} className="rounded-xl bg-muted/40 px-3 py-2">
                  <div className="text-muted-foreground font-medium">{r.bucket}</div>
                  <div className="text-foreground font-semibold mt-0.5">{formatMoney(r.billed, currency)}</div>
                  <div className="text-muted-foreground">{formatMoney(r.collected, currency)} collected</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
